import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
import { ErrorResponse, ErrorCode } from '../types/error';
import { AuthService } from '../services/AuthService';
import { LOGOUT_DELAY_MS } from './common';
import Logger from '../utils/logger';

interface AxiosRequestConfigWithMetadata extends AxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

class ErrorInterceptor {
  private errorHandler?: (error: ErrorResponse) => void;
  private validationErrorHandler?: (errors: any[]) => void;

  setErrorHandler(handler: (error: ErrorResponse) => void) {
    this.errorHandler = handler;
  }

  setValidationErrorHandler(handler: (errors: any[]) => void) {
    this.validationErrorHandler = handler;
  }

  private handleApiError(error: AxiosError<ErrorResponse>) {
    if (!error.response?.data) {
      this.handleNetworkError(error);
      return;
    }

    const errorData = error.response.data;
    const config = error.config as AxiosRequestConfigWithMetadata;
    const duration = config?.metadata ? Date.now() - config.metadata.startTime : undefined;
    
    const correlationIdHeader = error.response.headers?.[Logger.getCorrelationIdHeaderName().toLowerCase()] ||
                                 error.config?.headers?.[Logger.getCorrelationIdHeaderName()];
    const correlationId = correlationIdHeader ? String(correlationIdHeader) : undefined;

    const context = {
      method: error.config?.method?.toUpperCase(),
      path: errorData.path || error.config?.url,
      url: error.config?.url,
      correlationId,
      statusCode: errorData.status,
      duration,
    };

    try {
      const url = new URL(error.config?.url || '', window.location.origin);
      const queryParams: Record<string, string> = {};
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      if (Object.keys(queryParams).length > 0) {
        (context as any).queryParams = queryParams;
      }
    } catch (e) {
    }

    Logger.logErrorResponse('API error', errorData, context);

    switch (errorData.error) {
      case ErrorCode.UNAUTHORIZED:
        this.handleUnauthorizedError(errorData);
        break;
        
      case ErrorCode.FORBIDDEN:
        this.handleForbiddenError(errorData);
        break;
        
      case ErrorCode.VALIDATION_ERROR:
      case ErrorCode.BINDING_ERROR:
      case ErrorCode.CONSTRAINT_VIOLATION:
        this.handleValidationError(errorData);
        break;
        
      case ErrorCode.NOT_FOUND:
        this.handleNotFoundError(errorData);
        break;
        
      case ErrorCode.INTERNAL_SERVER_ERROR:
        this.handleInternalServerError(errorData);
        break;
        
      case ErrorCode.BAD_REQUEST:
      case ErrorCode.MISSING_PARAMETER:
      case ErrorCode.TYPE_MISMATCH:
      case ErrorCode.MALFORMED_REQUEST:
      case ErrorCode.INVALID_ARGUMENT:
      case ErrorCode.INVALID_SEARCH:
      case ErrorCode.METHOD_NOT_ALLOWED:
        this.handleBadRequestError(errorData);
        break;
        
      default:
        this.handleGenericError(errorData);
    }

    if (this.errorHandler) {
      this.errorHandler(errorData);
    }
  }

  private handleNetworkError(error: AxiosError) {
    const networkError: ErrorResponse = {
      status: 0,
      error: ErrorCode.NETWORK_ERROR,
      message: 'Network error. Please check your connection and try again.',
      details: 'Unable to connect to the server',
      path: error.config?.url || 'unknown',
      timestamp: new Date().toISOString()
    };

    const config = error.config as AxiosRequestConfigWithMetadata;
    const duration = config?.metadata ? Date.now() - config.metadata.startTime : undefined;
    const correlationIdHeader = error.config?.headers?.[Logger.getCorrelationIdHeaderName()];
    const correlationId = correlationIdHeader ? String(correlationIdHeader) : undefined;

    const context = {
      method: error.config?.method?.toUpperCase(),
      path: error.config?.url || 'unknown',
      url: error.config?.url,
      correlationId,
      duration,
    };

    Logger.error(
      'Network error',
      `Unable to connect to server: ${error.message || 'Connection failed'}`,
      context,
      error,
      {
        code: error.code,
        isNetworkError: !error.response,
      }
    );
    
    if (this.errorHandler) {
      this.errorHandler(networkError);
    }
  }

  private handleUnauthorizedError(errorData: ErrorResponse) {
    Logger.warn(
      'Authentication error',
      errorData.message || 'Unauthorized access attempt',
      {
        path: errorData.path,
        statusCode: errorData.status,
      },
      {
        error: errorData.error,
      }
    );

    if (this.errorHandler) {
      this.errorHandler(errorData);
    }
    
    setTimeout(() => {
      localStorage.removeItem('ACCESS_TOKEN');
      localStorage.removeItem('PROFILE');
      window.location.href = '/';
    }, LOGOUT_DELAY_MS);
  }

  private handleForbiddenError(errorData: ErrorResponse) {
    Logger.warn(
      'Access denied',
      errorData.message || 'Insufficient permissions',
      {
        path: errorData.path,
        statusCode: errorData.status,
      },
      {
        error: errorData.error,
      }
    );

    if (this.errorHandler) {
      this.errorHandler(errorData);
    }
  }

  private handleValidationError(errorData: ErrorResponse) {
    if (errorData.validationErrors) {
      Logger.warn(
        'Validation error',
        errorData.message || 'Validation failed',
        {
          path: errorData.path,
          statusCode: errorData.status,
        },
        {
          error: errorData.error,
          validationErrors: errorData.validationErrors,
          errorCount: errorData.validationErrors.length,
        }
      );

      if (this.validationErrorHandler) {
        this.validationErrorHandler(errorData.validationErrors);
      }
    } else {
      Logger.error(
        'Validation error',
        errorData.message || 'Validation failed',
        {
          path: errorData.path,
          statusCode: errorData.status,
        },
        undefined,
        {
          error: errorData.error,
        }
      );

      if (this.errorHandler) {
        this.errorHandler(errorData);
      }
    }
  }

  private handleNotFoundError(errorData: ErrorResponse) {
    if (this.errorHandler) {
      this.errorHandler(errorData);
    }
  }

  private handleInternalServerError(errorData: ErrorResponse) {
    if (this.errorHandler) {
      this.errorHandler(errorData);
    }
  }

  private handleBadRequestError(errorData: ErrorResponse) {
    if (this.errorHandler) {
      this.errorHandler(errorData);
    }
  }

  private handleGenericError(errorData: ErrorResponse) {
    if (this.errorHandler) {
      this.errorHandler(errorData);
    }
  }

  private showErrorNotification(title: string, message: string, details?: string) {
    console.error(`${title}: ${message}`, details);
    
    if (this.errorHandler) {
      const errorResponse: ErrorResponse = {
        status: 0,
        error: 'NOTIFICATION_ERROR',
        message: message,
        details: details,
        path: window.location.pathname,
        timestamp: new Date().toISOString()
      };
      this.errorHandler(errorResponse);
    }
  }

  setupInterceptors() {
    axios.interceptors.request.use(
      (config: AxiosRequestConfigWithMetadata) => {
        const token = AuthService.getToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers['X-Auth-Token'] = token;
        }
        
        const correlationId = Logger.getCorrelationId();
        config.headers = config.headers || {};
        config.headers[Logger.getCorrelationIdHeaderName()] = correlationId;
        
        config.metadata = { startTime: Date.now() };
        
        Logger.logApiRequest(
          config.method?.toUpperCase() || 'UNKNOWN',
          config.url || 'unknown',
          {
            correlationId,
            method: config.method?.toUpperCase(),
          }
        );
        
        return config;
      },
      (error) => {
        Logger.error(
          'Request interceptor error',
          'Failed to process request',
          {
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
          },
          error
        );
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response: AxiosResponse) => {
        const config = response.config as AxiosRequestConfigWithMetadata;
        const duration = config.metadata ? Date.now() - config.metadata.startTime : undefined;
        const correlationId = config.headers?.[Logger.getCorrelationIdHeaderName()] as string;

        Logger.logApiResponse(
          config.method?.toUpperCase() || 'UNKNOWN',
          config.url || 'unknown',
          response.status,
          duration || 0,
          {
            correlationId,
          }
        );

        return response;
      },
      (error: AxiosError<ErrorResponse>) => {
        this.handleApiError(error);
        return Promise.reject(error);
      }
    );
  }
}

const errorInterceptor = new ErrorInterceptor();
errorInterceptor.setupInterceptors();

export default errorInterceptor;
export { ErrorInterceptor };
